import React from 'react'
import {
	Alert,
	Box,
	Button,
	ButtonBase,
	Chip,
	CircularProgress,
	IconButton,
	Paper,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SaveIcon from '@mui/icons-material/Save'
import SendIcon from '@mui/icons-material/Send'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { mainBlue, mainGreen, mainRed } from '../../../utils/color'
import { accountTypeLabel, formatMoney, sectionSx } from '../cashflowUtils'
import { MoneyStat } from './CashflowCommon'
import OrderStatisticsTable from '../../../components/OrderStatisticsTable'

const CASH_ACCOUNT_NAME = 'Tiền mặt mặc định'
// Nền nhạt xen kẽ cho từng thẻ tài khoản trong phần đối soát
const ACCOUNT_CARD_TONES = [
	{ background: '#EAF5EE', accent: mainGreen },
	{ background: '#E8F1FB', accent: mainBlue },
]
const CONTROL_HEIGHT = 40
const CELL_BORDER = '1px solid #E5ECE7'

// Lưới nhãn-trái kẻ ô như bảng: cột nhãn nền xanh nhạt, cột giá trị nền trắng, mỗi hàng có đường kẻ
const auditGridSx = {
	display: 'grid',
	gridTemplateColumns: {
		xs: 'minmax(84px, 38%) minmax(0, 1fr)',
		md: 'repeat(2, minmax(96px, max-content) minmax(0, 1fr))',
		lg: 'repeat(3, minmax(96px, max-content) minmax(0, 1fr))',
	},
	alignItems: 'stretch',
	width: '100%',
	background: '#fff',
	'& > *:last-child': { borderBottom: 0 },
}

const fullRowSx = { gridColumn: '1 / -1', minWidth: 0 }
const fieldRestSx = { gridColumn: { xs: '2 / -1', md: '2 / -1' }, minWidth: 0 }

const labelSx = {
	fontSize: 11,
	fontWeight: 800,
	letterSpacing: '0.4px',
	lineHeight: 1.3,
	color: '#4A5A68',
	textTransform: 'uppercase',
}

const cellSx = {
	display: 'flex',
	alignItems: 'center',
	minWidth: 0,
	minHeight: CONTROL_HEIGHT + 16,
	px: 1.5,
	py: 1,
	borderBottom: CELL_BORDER,
}

// Cột nhãn tô nền để mắt bám được từng hàng, giống hàng tiêu đề của bảng chi phí
const labelCellSx = { ...cellSx, background: '#F5F9F6', borderRight: CELL_BORDER }

const valueCellSx = { ...cellSx, background: '#fff' }

const readOnlyValueSx = {
	minHeight: CONTROL_HEIGHT - 8,
	display: 'flex',
	alignItems: 'center',
	minWidth: 0,
	width: '100%',
}

const inputSx = {
	width: '100%',
	'& .MuiOutlinedInput-root': {
		height: CONTROL_HEIGHT,
		background: '#fff',
		borderRadius: '8px',
	},
}

const textareaSx = {
	width: '100%',
	'& .MuiOutlinedInput-root': {
		background: '#fff',
		borderRadius: '8px',
	},
}

const controlButtonSx = {
	height: CONTROL_HEIGHT,
	borderRadius: '8px',
	whiteSpace: 'nowrap',
}

const diffBoxSx = {
	height: CONTROL_HEIGHT,
	borderRadius: '8px',
	py: 0,
	px: 1.25,
	alignItems: 'center',
	'& .MuiAlert-icon': { alignItems: 'center', mr: 0.75, py: 0 },
	'& .MuiAlert-message': {
		fontVariantNumeric: 'tabular-nums',
		fontWeight: 800,
		p: 0,
		whiteSpace: 'nowrap',
	},
}

function FieldLabel({ children }) {
	return <Typography sx={labelSx}>{children}</Typography>
}

// Fragment nên hai ô vẫn là con trực tiếp của lưới, giữ được canh cột và đường kẻ liền mạch
function Field({ label, children, wide }) {
	return (
		<>
			<Box sx={labelCellSx}>
				<FieldLabel>{label}</FieldLabel>
			</Box>
			<Box sx={wide ? { ...valueCellSx, ...fieldRestSx } : valueCellSx}>{children}</Box>
		</>
	)
}

// Số chỉ để xem thì bỏ khung, chỉ còn chữ trong ô của bảng cho khỏi rối
function ReadOnlyAmount({ color = '#111', value }) {
	return (
		<Box sx={readOnlyValueSx}>
			<Typography
				noWrap
				sx={{ color, fontWeight: 800, fontSize: 15, fontVariantNumeric: 'tabular-nums', width: '100%' }}>
				{formatMoney(value)}
			</Typography>
		</Box>
	)
}

// Hàng tiêu đề nhóm, tô nền như hàng head của bảng chi phí
function SectionHeading({ children }) {
	return (
		<Typography
			sx={{
				...fullRowSx,
				...labelSx,
				color: mainGreen,
				background: '#F1F7F3',
				px: 1.5,
				py: 1,
				borderBottom: CELL_BORDER,
			}}>
			{children}
		</Typography>
	)
}

function ActionRow({ children }) {
	return (
		<Box
			sx={{
				...fullRowSx,
				display: 'flex',
				justifyContent: 'flex-end',
				px: 1.5,
				py: 1.25,
				background: '#FAFCFB',
				borderBottom: CELL_BORDER,
			}}>
			{children}
		</Box>
	)
}

function SaveControl({ disabled, label, saving, onClick }) {
	return (
		<Button
			variant='contained'
			startIcon={saving ? <CircularProgress color='inherit' size={16} /> : <SaveIcon />}
			sx={{ ...controlButtonSx, background: mainGreen, px: 2.5 }}
			disabled={disabled}
			onClick={onClick}>
			{label}
		</Button>
	)
}

function ScreenshotField({ alt, buttonLabel, disabled, items, multiple, onPick, onRemove, onView, required }) {
	return (
		<Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap sx={{ minWidth: 0 }}>
			<Button
				component='label'
				variant='outlined'
				startIcon={<UploadFileIcon />}
				sx={controlButtonSx}
				disabled={disabled}>
				{buttonLabel}
				<input
					hidden
					multiple={multiple}
					type='file'
					accept='image/*'
					onChange={(event) => {
						onPick(event.target.files)
						event.target.value = ''
					}}
				/>
			</Button>
			{items.map((item, index) => (
				<Box key={item.key} sx={{ position: 'relative', flex: '0 0 auto' }}>
					<Tooltip title='Xem ảnh'>
						<ButtonBase
							aria-label={`${alt} ${index + 1}`}
							onClick={() => onView(item, index)}
							sx={{
								width: CONTROL_HEIGHT,
								height: CONTROL_HEIGHT,
								borderRadius: '8px',
								overflow: 'hidden',
								border: '1px solid #E3E8EF',
								background: '#F4F6F8',
							}}>
							<Box
								component='img'
								loading='lazy'
								src={item.url}
								alt={`${alt} ${index + 1}`}
								sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
							/>
						</ButtonBase>
					</Tooltip>
					{onRemove && (
						<IconButton
							size='small'
							aria-label={`Xóa ${alt} ${index + 1}`}
							onClick={() => onRemove(item)}
							sx={{
								position: 'absolute',
								top: -7,
								right: -7,
								width: 18,
								height: 18,
								background: '#fff',
								border: '1px solid #E3E8EF',
								'&:hover': { background: '#FFEBEE' },
							}}>
							<CloseIcon sx={{ fontSize: 12 }} />
						</IconButton>
					)}
				</Box>
			))}
			{required && items.length === 0 && (
				<Typography sx={{ color: mainRed, fontSize: 12, fontWeight: 700 }}>
					Bắt buộc có ảnh mới cập nhật được
				</Typography>
			)}
		</Stack>
	)
}

export default function ReportTab({
	actualClosingTotal,
	addBankFiles,
	addCashFiles,
	adjustedClosingTotal,
	bankDifference,
	canSubmitReport,
	cashDifference,
	getAdjustedClosingBalance,
	getAdjustedCashClosingBalance,
	getBankOpeningScreenshotUrl,
	getBankScreenshotItems,
	getCashScreenshotItems,
	getReconciliationStatus,
	isDateLocked,
	openingDifference,
	orderStatistics,
	pendingApprovalExpenseCount,
	removeBankScreenshot,
	removeCashScreenshot,
	reportForm,
	savedAccountVerification,
	savingAccountId,
	setImageViewer,
	submitCashClosingVerification,
	submitClosingVerification,
	submitOpeningVerification,
	submitReport,
	summary,
	totalDifference,
	unclassifiedIncomeOrderCount,
	updateBankOpeningFile,
	updateBankVerification,
	updateCashVerification,
}) {
	const reconciliationStatus = getReconciliationStatus()
	const cashClosingDiff = cashDifference()
	const cashScreenshotItems = getCashScreenshotItems()
	const isSavingCashClosing = savingAccountId === 'cash:closing'
	const cashCrossStoreOutgoing = Number(summary?.income?.cashCrossStoreOutgoing) || 0
	const transferCrossStoreOutgoing = Number(summary?.income?.transferCrossStoreOutgoing) || 0
	const appCrossStoreOutgoing = Number(summary?.income?.appCrossStoreOutgoing) || 0
	const orderCountMeta = (count) => `${Number(count) || 0} đơn`
	const cashOrderCount =
		summary?.orders?.cashIncomeCount ?? summary?.orders?.cash ?? summary?.orders?.cashOrders?.length
	const transferOrderCount =
		summary?.orders?.transferIncomeCount ?? summary?.orders?.transfer ?? summary?.orders?.transferOrders?.length
	const appOrderCount = summary?.orders?.appIncomeCount ?? summary?.orders?.app ?? summary?.orders?.appOrders?.length
	const statisticsRevenue = (statKey) => {
		const value = orderStatistics?.[statKey]?.totalRevenue
		return value === undefined || value === null ? null : Number(value)
	}
	const statisticsChecks = [
		{
			key: 'cash',
			label: 'Thu tiền mặt',
			reportValue: summary?.income?.cashIncome,
			statisticsValue: statisticsRevenue('cashOrders'),
		},
		{
			key: 'transfer',
			label: 'Thu chuyển khoản',
			reportValue: summary?.income?.transferIncome,
			statisticsValue: statisticsRevenue('transferOrders'),
		},
		{
			key: 'app',
			label: 'Thu App',
			reportValue: summary?.income?.appIncome,
			statisticsValue: statisticsRevenue('appOrders'),
		},
	]
		.filter((check) => check.statisticsValue !== null)
		.map((check) => ({ ...check, difference: (Number(check.reportValue) || 0) - check.statisticsValue }))
	const statisticsMismatches = statisticsChecks.filter((check) => check.difference !== 0)
	const statisticsCheck = (key) => statisticsChecks.find((item) => item.key === key)
	const statisticsMeta = (count, key) => {
		const check = statisticsCheck(key)
		if (!check) return orderCountMeta(count)
		return check.difference === 0
			? `${orderCountMeta(count)} · khớp thống kê`
			: `${orderCountMeta(count)} · lệch ${formatMoney(check.difference)}`
	}
	const statisticsAlert = (key) => {
		const check = statisticsCheck(key)
		return Boolean(check) && check.difference !== 0
	}
	const crossStoreOutgoingText = [
		cashCrossStoreOutgoing > 0 ? `${formatMoney(cashCrossStoreOutgoing)} tiền mặt` : '',
		transferCrossStoreOutgoing > 0 ? `${formatMoney(transferCrossStoreOutgoing)} chuyển khoản` : '',
		appCrossStoreOutgoing > 0 ? `${formatMoney(appCrossStoreOutgoing)} App` : '',
	]
		.filter(Boolean)
		.join(' và ')
	// "Thu trong ngày" = doanh thu tiền mặt + khoản thu bất thường, chênh với ô "Thu tiền mặt" đúng bằng phần thu bất thường
	const cashManualIncome = Number(summary?.income?.cashManualIncome) || 0
	const cashIncomeBreakdown = cashManualIncome
		? `Thu tiền mặt ${formatMoney(summary?.income?.cashIncome)} + thu bất thường ${formatMoney(cashManualIncome)}`
		: 'Bằng ô "Thu tiền mặt" (tổng tiền đơn đã gồm ship)'
	// "Thực thu chuyển khoản" = doanh thu chuyển khoản + khoản thu bất thường vào TK ngân hàng, khớp số nhập tay cuối ngày
	const transferManualIncome = Number(summary?.income?.transferManualIncome) || 0
	const transferGrossIncome =
		summary?.income?.transferGrossIncome ?? (Number(summary?.income?.transferIncome) || 0) + transferManualIncome
	const transferGrossIncomeMeta = `Thu CK ${formatMoney(summary?.income?.transferIncome)} + thu bất thường ${formatMoney(transferManualIncome)}`
	const pendingExpenseCount = pendingApprovalExpenseCount ?? summary?.expenses?.pendingApprovalItems?.length ?? 0
	const pendingExpenseTotal = Number(summary?.expenses?.pendingApprovalTotal) || 0
	const unassignedOrderCount = Number(unclassifiedIncomeOrderCount) || 0
	const submitBlockReason = isDateLocked
		? 'Đã qua ngày báo cáo, không thể gửi báo cáo'
		: unassignedOrderCount > 0
			? `Còn ${unassignedOrderCount} đơn chưa gán tài khoản thu`
			: pendingExpenseCount > 0
				? `Còn ${pendingExpenseCount} khoản chi chờ xác nhận`
				: ''
	const canSendReport = canSubmitReport && !submitBlockReason

	const viewScreenshot = (item, index, title) =>
		setImageViewer({ open: true, url: item.url, title: `${title} ${index + 1}` })

	// Phần đối soát rất dài nên các thẻ tài khoản đổi nền so le cho đỡ lẫn vào nhau
	const renderAccountCard = ({ chips, children, title, index = 0 }) => {
		const tone = ACCOUNT_CARD_TONES[index % ACCOUNT_CARD_TONES.length]
		return (
			<Paper
				elevation={0}
				sx={{
					border: '1px solid #DCE8DF',
					borderLeft: `4px solid ${tone.accent}`,
					borderRadius: '12px',
					background: '#fff',
					overflow: 'hidden',
				}}>
				<Stack
					direction='row'
					spacing={1}
					alignItems='center'
					flexWrap='wrap'
					useFlexGap
					sx={{ px: 1.5, py: 1.25, background: tone.background, borderBottom: '1px solid #DCE8DF' }}>
					<Typography sx={{ ...labelSx, fontSize: 13, color: mainBlue }}>{title}</Typography>
					{chips}
				</Stack>
				<Box sx={auditGridSx}>{children}</Box>
			</Paper>
		)
	}

	return (
		<Box>
			{isDateLocked && (
				<Alert severity='warning' sx={{ mb: 2 }}>
					Ngày này đã qua nên toàn bộ thao tác thu chi đã bị khóa, chỉ còn xem lại số liệu.
				</Alert>
			)}
			<Box
				sx={{
					display: 'grid',
					gap: 2,
					mb: 2,
					gridTemplateColumns: {
						xs: '1fr',
						sm: 'repeat(2, minmax(0, 1fr))',
						md: 'repeat(3, minmax(0, 1fr))',
						lg: 'repeat(4, minmax(0, 1fr))',
					},
				}}>
				<MoneyStat
					label='Thu tiền mặt'
					value={summary?.income?.cashIncome}
					color={mainGreen}
					meta={statisticsMeta(cashOrderCount, 'cash')}
					alert={statisticsAlert('cash')}
				/>
				<MoneyStat
					label='Thu chuyển khoản'
					value={summary?.income?.transferIncome}
					color={mainBlue}
					meta={statisticsMeta(transferOrderCount, 'transfer')}
					alert={statisticsAlert('transfer')}
				/>
				<MoneyStat
					label='Thực thu chuyển khoản'
					value={transferGrossIncome}
					color={mainBlue}
					meta={transferGrossIncomeMeta}
				/>
				<MoneyStat
					label='Thu App'
					value={summary?.income?.appIncome}
					color='#006C67'
					meta={statisticsMeta(appOrderCount, 'app')}
					alert={statisticsAlert('app')}
				/>
				<MoneyStat label='Tổng chi' value={summary?.expenses?.totalSpendExpense} color={mainRed} />
				<MoneyStat label='Tổng hệ thống' value={adjustedClosingTotal} color='#111' />
				<MoneyStat label='Tổng thực tế' value={actualClosingTotal} color={mainBlue} />
				<MoneyStat
					label='Chênh lệch tổng'
					value={totalDifference}
					color={totalDifference === 0 ? mainGreen : mainRed}
				/>
				<MoneyStat label='Rút tiền' value={summary?.expenses?.totalCashoutExpense} color='#9C1C4A' />
			</Box>
			{orderStatistics && (
				<Box sx={{ mb: 2 }}>
					<Typography sx={{ fontWeight: 800, mb: 1 }}>Thống kê cửa hàng</Typography>
					{statisticsMismatches.length > 0 ? (
						<Alert severity='warning' sx={{ mb: 1.5 }}>
							Số thu lệch so với thống kê cửa hàng:{' '}
							{statisticsMismatches.map((check) => `${check.label} lệch ${formatMoney(check.difference)}`).join(', ')}.
							Cần kiểm tra lại trước khi gửi báo cáo.
						</Alert>
					) : (
						<Alert severity='success' sx={{ mb: 1.5 }}>
							Thu tiền mặt, Thu chuyển khoản và Thu App khớp với thống kê cửa hàng.
						</Alert>
					)}
					<Box className='cashflow-table-shell cashflow-scroll'>
						<OrderStatisticsTable statistics={orderStatistics} sx={{ mb: 0, minWidth: 520 }} />
					</Box>
				</Box>
			)}
			{crossStoreOutgoingText && (
				<Alert severity='info' sx={{ mb: 2 }}>
					Đã loại khỏi báo cáo {crossStoreOutgoingText} từ đơn nhận đã được cửa hàng chuyển xác nhận thu tiền.
				</Alert>
			)}
			{unassignedOrderCount > 0 && (
				<Alert severity='warning' sx={{ mb: 2 }}>
					Còn {unassignedOrderCount} đơn chưa gán tài khoản thu, cần gán hết ở tab "Gán TK đơn" mới gửi được báo cáo.
				</Alert>
			)}
			{summary?.income?.unassignedTransferIncome > 0 && (
				<Alert severity='warning' sx={{ mb: 2 }}>
					Có {formatMoney(summary.income.unassignedTransferIncome)} chuyển khoản chưa gán tài khoản. Nếu cửa hàng có
					nhiều tài khoản, cần cập nhật tài khoản nhận tiền cho đơn hàng hoặc đối soát thủ công.
				</Alert>
			)}
			{pendingExpenseCount > 0 && (
				<Alert severity='warning' sx={{ mb: 2 }}>
					Có {pendingExpenseCount} khoản chi chờ xác nhận ({formatMoney(pendingExpenseTotal)}) chưa tính vào báo cáo.
				</Alert>
			)}
			{summary?.existingReport?.submissionHistory?.length > 0 && (
				<Paper className="pdf-ignore" elevation={0} sx={{ ...sectionSx, mb: 2 }}>
					<Typography sx={{ fontWeight: 800, mb: 1 }}>Lịch sử gửi báo cáo</Typography>
					<Stack spacing={1}>
						{summary.existingReport.submissionHistory.map((item) => (
							<Stack
								key={`${item.version}-${item.submittedAt}`}
								direction='row'
								spacing={1}
								alignItems='center'
								flexWrap="nowrap"
								useFlexGap>
								<Chip
									color='success'
									label={`Lần ${item.version} - ${new Date(item.submittedAt).toLocaleString('vi-VN')}`}
								/>
								<Typography sx={{ fontSize: 13, fontWeight: 700, color: '#4A5A68' }}>
									Người gửi: {item.submittedBy?.name || item.submittedBy?.email || 'Không xác định'}
								</Typography>
							</Stack>
						))}
					</Stack>
				</Paper>
			)}
			<Paper elevation={0} sx={sectionSx}>
				<Stack
					direction={{ xs: 'column', md: 'row' }}
					spacing={1}
					alignItems={{ xs: 'stretch', md: 'center' }}
					justifyContent='space-between'
					sx={{ mb: 2 }}>
					<Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
						<Typography sx={{ ...labelSx, fontSize: 13, color: mainBlue }}>Đối soát tài khoản thu chi</Typography>
						<Chip size='small' color={reconciliationStatus.color} label={reconciliationStatus.label} />
						<Chip
							size='small'
							color={summary?.existingReport?.status === 'submitted' ? 'success' : 'default'}
							label={
								summary?.existingReport?.status === 'submitted'
									? `Đã gửi lần ${summary?.existingReport?.submitCount || 1}`
									: 'Bản nháp'
							}
						/>
					</Stack>
					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
						<Button
							variant='outlined'
							startIcon={<SaveIcon />}
							sx={controlButtonSx}
							disabled={!canSubmitReport}
							onClick={() => submitReport('draft')}>
							Lưu nháp
						</Button>
						<Tooltip title={submitBlockReason}>
							<Box component='span'>
								<Button
									variant='contained'
									startIcon={<SendIcon />}
									sx={{ ...controlButtonSx, background: mainRed, '&:hover': { background: '#A32222' }, px: 2.5 }}
									disabled={!canSendReport}
									onClick={() => submitReport('submitted')}>
									Gửi báo cáo
								</Button>
							</Box>
						</Tooltip>
					</Stack>
				</Stack>
				<Stack spacing={2}>
					{renderAccountCard({
						index: 0,
						title: CASH_ACCOUNT_NAME,
						chips: (
							<>
								<Chip size='small' label='Tiền mặt' />
								<Chip
									size='small'
									color={reportForm.cashActualBalance !== '' ? 'success' : 'default'}
									label={reportForm.cashActualBalance !== '' ? 'Đã cập nhật' : 'Chưa cập nhật'}
								/>
							</>
						),
						children: (
							<>
								<SectionHeading>Số liệu trong ngày</SectionHeading>
								<Field label='Đầu ngày'>
									<ReadOnlyAmount value={summary?.openingBalances?.cash} />
								</Field>
								<Field label='Thu trong ngày'>
									<Tooltip title={cashIncomeBreakdown}>
										<Box>
											<ReadOnlyAmount
												value={summary?.income?.cashGrossIncome ?? summary?.income?.cashIncome}
												color={mainGreen}
											/>
										</Box>
									</Tooltip>
								</Field>
								<Field label='Chi trong ngày'>
									<ReadOnlyAmount value={summary?.expenses?.totalCashExpense} color={mainRed} />
								</Field>

								<SectionHeading>Đối soát cuối ngày</SectionHeading>
								<Field label='Cuối ngày hệ thống'>
									<ReadOnlyAmount value={getAdjustedCashClosingBalance()} color={mainBlue} />
								</Field>
								<Field label='Số cuối ngày thực tế'>
									<TextField
										size='small'
										sx={inputSx}
										inputProps={{ 'aria-label': 'Số cuối ngày thực tế' }}
										value={reportForm.cashActualBalance}
										onChange={(event) => updateCashVerification('cashActualBalance', event.target.value)}
										disabled={!canSubmitReport}
									/>
								</Field>
								<Field label='Chênh lệch'>
									<Alert severity={cashClosingDiff === 0 ? 'success' : 'error'} sx={diffBoxSx}>
										{formatMoney(cashClosingDiff)}
									</Alert>
								</Field>
								<Field wide label='Ảnh đối soát *'>
									<ScreenshotField
										multiple
										required
										alt={`Ảnh đối soát - ${CASH_ACCOUNT_NAME}`}
										buttonLabel='Chọn ảnh'
										disabled={!canSubmitReport}
										items={cashScreenshotItems}
										onPick={addCashFiles}
										onRemove={canSubmitReport ? removeCashScreenshot : undefined}
										onView={(item, index) => viewScreenshot(item, index, `Ảnh đối soát - ${CASH_ACCOUNT_NAME}`)}
									/>
								</Field>
								<Field wide label='Ghi chú'>
									<TextField
										size='small'
										sx={textareaSx}
										inputProps={{ 'aria-label': 'Ghi chú' }}
										value={reportForm.cashExplanation || ''}
										onChange={(event) => updateCashVerification('cashExplanation', event.target.value)}
										disabled={!canSubmitReport}
										multiline
										minRows={2}
									/>
								</Field>
								<ActionRow>
									<SaveControl
										label='Cập nhật cuối ngày'
										saving={isSavingCashClosing}
										disabled={!canSubmitReport || isSavingCashClosing || cashScreenshotItems.length === 0}
										onClick={submitCashClosingVerification}
									/>
								</ActionRow>
							</>
						),
					})}
					{summary?.closingBalances?.banks?.length === 0 && (
						<Alert severity='info'>Chưa có tài khoản ngân hàng để đối soát.</Alert>
					)}
					{summary?.closingBalances?.banks?.map((bankBalance, bankIndex) => {
						const accountId = bankBalance.bankAccount._id
						const accountName = bankBalance.bankAccount.name
						const verification = reportForm.bankVerifications[accountId] || {}
						const diff = bankDifference(bankBalance)
						const accountType = bankBalance.bankAccount.accountType || 'bank'
						// Kênh App quyết toán mỗi ngày: đầu ngày luôn 0 và không chi trực tiếp từ App
						const isAppAccount = accountType === 'app'
						const savedVerification = savedAccountVerification(accountId)
						const isSavingOpening = savingAccountId === `${accountId}:opening`
						const isSavingClosing = savingAccountId === `${accountId}:closing`
						const openingDiff = openingDifference(bankBalance)
						const openingScreenshotUrl = getBankOpeningScreenshotUrl(accountId, verification)
						const screenshotItems = getBankScreenshotItems(accountId, verification)
						return (
							<React.Fragment key={accountId}>
								{renderAccountCard({
									index: bankIndex + 1,
									title: accountName,
									chips: (
										<>
											<Chip size='small' label={accountTypeLabel(accountType)} />
											<Chip
												size='small'
												color={savedVerification ? 'success' : 'default'}
												label={savedVerification ? 'Đã cập nhật' : 'Chưa cập nhật'}
											/>
										</>
									),
									children: (
										<>
											<SectionHeading>Số liệu trong ngày</SectionHeading>
											{!isAppAccount && (
												<Field label='Đầu ngày hệ thống'>
													<ReadOnlyAmount value={bankBalance.openingBalance} />
												</Field>
											)}
											<Field label='Thu trong ngày'>
												<ReadOnlyAmount value={bankBalance.income} color={mainGreen} />
											</Field>
											{!isAppAccount && (
												<Field label='Chi trong ngày'>
													<ReadOnlyAmount value={bankBalance.expense} color={mainRed} />
												</Field>
											)}

											{!isAppAccount && (
												<>
													<SectionHeading>Đối soát đầu ngày</SectionHeading>
													<Field label='Số đầu ngày thực tế'>
														<TextField
															size='small'
															sx={inputSx}
															inputProps={{ 'aria-label': 'Số đầu ngày thực tế' }}
															value={verification.openingActualBalance}
															onChange={(event) =>
																updateBankVerification(accountId, 'openingActualBalance', event.target.value)
															}
															disabled={!canSubmitReport}
														/>
													</Field>
													<Field label='Chênh lệch đầu ngày'>
														<Alert severity={openingDiff === 0 ? 'success' : 'error'} sx={diffBoxSx}>
															{formatMoney(openingDiff)}
														</Alert>
													</Field>
													<Field label='Ảnh đầu ngày'>
														<ScreenshotField
															alt={`Ảnh đầu ngày - ${accountName}`}
															buttonLabel='Chọn ảnh'
															disabled={!canSubmitReport}
															items={openingScreenshotUrl ? [{ key: 'opening', url: openingScreenshotUrl }] : []}
															onPick={(files) => updateBankOpeningFile(accountId, files?.[0])}
															onView={(item) =>
																setImageViewer({ open: true, url: item.url, title: `Ảnh đầu ngày - ${accountName}` })
															}
														/>
													</Field>
													{(openingDiff !== 0 || verification.openingExplanation) && (
														<Field wide label='Ghi chú đầu ngày'>
															<TextField
																size='small'
																sx={inputSx}
																inputProps={{ 'aria-label': 'Ghi chú đầu ngày' }}
																value={verification.openingExplanation || ''}
																onChange={(event) =>
																	updateBankVerification(accountId, 'openingExplanation', event.target.value)
																}
																disabled={!canSubmitReport}
															/>
														</Field>
													)}
													<ActionRow>
														<SaveControl
															label='Cập nhật đầu ngày'
															saving={isSavingOpening}
															disabled={!canSubmitReport || isSavingOpening || isSavingClosing}
															onClick={() => submitOpeningVerification(bankBalance)}
														/>
													</ActionRow>
												</>
											)}

											<SectionHeading>Đối soát cuối ngày</SectionHeading>
											<Field label='Cuối ngày hệ thống'>
												<ReadOnlyAmount value={getAdjustedClosingBalance(bankBalance)} color={mainBlue} />
											</Field>
											<Field label='Số cuối ngày thực tế'>
												<TextField
													size='small'
													sx={inputSx}
													inputProps={{ 'aria-label': 'Số cuối ngày thực tế' }}
													value={verification.actualBalance}
													onChange={(event) => updateBankVerification(accountId, 'actualBalance', event.target.value)}
													disabled={!canSubmitReport}
												/>
											</Field>
											<Field label='Chênh lệch'>
												<Alert severity={diff === 0 ? 'success' : 'error'} sx={diffBoxSx}>
													{formatMoney(diff)}
												</Alert>
											</Field>
											<Field wide label='Ảnh đối soát *'>
												<ScreenshotField
													multiple
													required
													alt={`Ảnh đối soát - ${accountName}`}
													buttonLabel='Chọn ảnh'
													disabled={!canSubmitReport}
													items={screenshotItems}
													onPick={(files) => addBankFiles(accountId, files)}
													onRemove={canSubmitReport ? (item) => removeBankScreenshot(accountId, item) : undefined}
													onView={(item, index) => viewScreenshot(item, index, `Ảnh đối soát - ${accountName}`)}
												/>
											</Field>
											<Field wide label='Ghi chú'>
												<TextField
													size='small'
													sx={textareaSx}
													inputProps={{ 'aria-label': 'Ghi chú' }}
													value={verification.explanation || ''}
													onChange={(event) => updateBankVerification(accountId, 'explanation', event.target.value)}
													disabled={!canSubmitReport}
													multiline
													minRows={2}
												/>
											</Field>
											<ActionRow>
												<SaveControl
													label='Cập nhật cuối ngày'
													saving={isSavingClosing}
													disabled={
														!canSubmitReport || isSavingOpening || isSavingClosing || screenshotItems.length === 0
													}
													onClick={() => submitClosingVerification(bankBalance)}
												/>
											</ActionRow>
										</>
									),
								})}
							</React.Fragment>
						)
					})}
				</Stack>
			</Paper>
		</Box>
	)
}
